<template>
  <PortalLayout section="Portal Settings" hideHeader>
    <h1 class="py-3 text-xl border-b flex">
      <div class="flex-grow">
        <div class="font-bold">Portal Settings</div>
        <div>
          {{ portal?.name }}
          <span v-if="!portal?.isActive" class="font-bold text-sm">(disabled)</span>
        </div>
      </div>
      <EditButton v-if="!isPropertyLoading && !isLoading" @click="editClick">
        Edit
      </EditButton>
    </h1>
    <Loading v-if="isPropertyLoading || isLoading" />
    <div v-else>
      <div class="lg:flex flex-wrap">
        <div class="mt-4 pr-4 lg:w-1/3">
          <h1 class="font-bold">
            Portal Address<span v-if="portal?.siteCodeAlias">es</span>
          </h1>
          <router-link
            :to="'/' + portal?.siteCode"
            target="_blank"
            class="font-mono underline break-all"
          >
            {{ currentPageLocation }}/<span class="font-bold">
              {{ portal?.siteCode }}
            </span>
          </router-link>
          <router-link
            v-if="portal?.siteCodeAlias"
            :to="'/' + portal?.siteCodeAlias"
            target="_blank"
            class="block font-mono underline break-all"
          >
            {{ currentPageLocation }}/<span class="font-bold">
              {{ portal?.siteCodeAlias }}
            </span>
          </router-link>
        </div>
        <div class="mt-4 pr-4 lg:w-1/3">
          <h1 class="font-bold">Property Type</h1>
          <p>{{ propertyTypes[portal?.propertyType - 1].name }}</p>
        </div>
        <div class="mt-4 pr-4 lg:w-1/3">
          <h1 class="font-bold">Client {{ `${client?.categoryAlias ?? "Category"}` }}</h1>
          <p>{{ portal?.category }}</p>
        </div>
        <div class="mt-4 pr-4 w-1/3">
          <div class="font-bold">Floor List</div>
          <div v-if="portal?.floorList?.length ?? 0 > 0">
            <span
              class="font-mono text-sm"
              v-for="(floor, i) in portal?.floorList"
              :key="floor"
            >
              {{ floor }}<span v-if="i + 1 !== portal?.floorList.length">, </span>
            </span>
          </div>
          <div v-else>
            <span class="mr-2">No Floors Specified</span>
          </div>
          <div v-if="portal?.defaultFloor">
            <span class="font-bold">Default: </span>
            <span class="font-mono text-sm">{{ portal?.defaultFloor }}</span>
          </div>
        </div>
        <div class="mt-4 pr-4 w-1/3" v-if="portal?.domainWhiteList?.length ?? 0 > 0">
          <div class="font-bold">Domain White List</div>
          <div>
            <span
              class="font-mono text-sm"
              v-for="(floor, i) in portal?.domainWhiteList"
              :key="floor"
            >
              {{ floor }}<span v-if="i + 1 !== portal?.domainWhiteList.length">, </span>
            </span>
          </div>
        </div>
        <div class="mt-4 pr-4 lg:w-1/3">
          <h1 class="font-bold">Tenant Alias</h1>
          <p>{{ portal?.tenantAlias }}</p>
        </div>
        <div class="mt-4 pr-4 lg:w-1/3">
          <h1 class="font-bold">Response Library</h1>
          <p>
            {{
              responseLibOptions.find((l) => l.value === portal?.responseLibId)?.name ??
              "None."
            }}
          </p>
        </div>
        <div class="mt-4 mb-1 w-full">
          <div class="font-bold mb-1">Optional Features</div>
          <div class="flex flex-wrap flex-col h-full lg:h-1/2 xl:h-1/3">
            <div class="flex pb-1 pr-4">
              <div class="my-auto">
                <IHeroiconsOutlineCheck
                  class="bg-green-200"
                  v-if="portal?.isTrainingEnabled"
                />
                <IHeroiconsOutlineX v-else />
              </div>
              <span class="ml-2">Training</span>
            </div>
            <div class="flex pb-1 pr-4">
              <div class="my-auto">
                <IHeroiconsOutlineCheck
                  class="bg-green-200"
                  v-if="portal?.isRespondEnabled"
                />
                <IHeroiconsOutlineX v-else />
              </div>
              <span class="ml-2">Respond</span>
            </div>
            <div class="flex pb-1 pr-4">
              <div class="my-auto">
                <IHeroiconsOutlineCheck
                  class="bg-green-200"
                  v-if="portal?.respondAnonView"
                />
                <IHeroiconsOutlineX v-else />
              </div>
              <span class="ml-2">Respond Anonymous Viewing</span>
            </div>
            <div class="flex pb-1 pr-4">
              <div class="my-auto">
                <IHeroiconsOutlineCheck
                  class="bg-green-200"
                  v-if="portal?.isStaffDirEnabled"
                />
                <IHeroiconsOutlineX v-else />
              </div>
              <span class="ml-2">Staff Directory</span>
            </div>
            <div class="flex pb-1 pr-4">
              <div class="my-auto">
                <IHeroiconsOutlineCheck
                  class="bg-green-200"
                  v-if="portal?.isAlertEnabled"
                />
                <IHeroiconsOutlineX v-else />
              </div>
              <span class="ml-2">Alerts</span>
            </div>
            <div class="flex pb-1 pr-4">
              <div class="my-auto">
                <IHeroiconsOutlineCheck
                  class="bg-green-200"
                  v-if="portal?.isMobilityCheckEnabled"
                />
                <IHeroiconsOutlineX v-else />
              </div>
              <span class="ml-2">Mobility Check</span>
            </div>
            <div class="flex pb-1 pr-4">
              <div class="my-auto">
                <IHeroiconsOutlineCheck
                  class="bg-green-200"
                  v-if="portal?.isVrtourEnabled"
                />
                <IHeroiconsOutlineX v-else />
              </div>
              <span class="ml-2">VR Tour</span>
            </div>
            <div class="flex pb-1 pr-4">
              <div class="my-auto">
                <IHeroiconsOutlineCheck
                  class="bg-green-200"
                  v-if="portal?.usesSharedPasswordMode"
                />
                <IHeroiconsOutlineX v-else />
              </div>
              <span class="ml-2">Shared Passwords</span>
            </div>
            <div class="flex pb-1 pr-4">
              <div class="my-auto">
                <IHeroiconsOutlineCheck
                  class="bg-green-200"
                  v-if="portal?.hideAllAnonymousContent"
                />
                <IHeroiconsOutlineX v-else />
              </div>
              <span class="ml-2">Hide All Anonymous Content</span>
            </div>
            <div class="flex pb-1 pr-4">
              <div class="my-auto">
                <IHeroiconsOutlineCheck
                  class="bg-green-200"
                  v-if="portal?.isSSORegistrationEnabled"
                />
                <IHeroiconsOutlineX v-else />
              </div>
              <span class="ml-2">SSO 1st-visit screen</span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="portal?.accessibilityNotificationEmail" class="mt-4">
        <h2 class="font-bold">Accessibility Notification Email</h2>
        <p class="text-sm italic text-gray-800">
          A notification email will be sent to this address when a user registers as
          having Impaired Mobility
        </p>
        <div class="font-mono mt-1 underline">
          {{ portal?.accessibilityNotificationEmail }}
        </div>
      </div>
      <!-- <div v-else>No Accessibility Notification Email set</div> -->

      <div class="flex flex-wrap">
        <!-- Emergency Numbers -->
        <router-link to="numbers">
          <section class="my-4 mr-4 p-4 bg-gray-100 hover:bg-gray-200 cursor-pointer">
            <h1 class="font-bold mb-2 border-b-2">Emergency Numbers</h1>
            <div v-if="portal?.numbers">
              <div v-for="number in portal?.numbers" :key="number.numberId" class="">
                <div class="border-b py-1">
                  <div class="text-sm">{{ number.label }}</div>
                  <div class="text-sm ml-4 font-mono border-b">
                    {{ number.emergencyNumber }}
                  </div>
                </div>
              </div>
            </div>
            <div v-else>(No Emergency Numbers. Click to Add)</div>
          </section>
        </router-link>

        <!-- Documents -->
        <router-link to="documents">
          <section class="my-4 mr-4 p-4 bg-gray-100 hover:bg-gray-200 cursor-pointer">
            <h1 class="font-bold mb-2 border-b-2">Documents</h1>
            <div v-if="portal?.documents && portal?.documents?.length > 0">
              <div
                class="flex flex-row flex-wrap"
                v-for="doc in portal?.documents"
                :key="doc.documentId"
              >
                <div class="flex-1 text-sm border-b">{{ doc.label }}</div>
              </div>
            </div>
            <div v-else>(No Documents. Click to Add)</div>
          </section>
        </router-link>

        <!-- Responses -->
        <router-link to="respond">
          <section class="my-4 mr-4 p-4 bg-gray-100 hover:bg-gray-200 cursor-pointer">
            <h1 class="font-bold mb-2 border-b-2">Responses</h1>
            <div class="flex flex-row flex-wrap">
              <div v-for="step in responseTypeOptions" :key="step.value">
                <div class="flex-1 text-sm border-b">{{ step.name }}</div>
              </div>
            </div>
            <div v-if="!responseTypeOptions">(No Responses Added. Click to Add)</div>
          </section>
        </router-link>

        <!-- Roles -->
        <router-link to="roles">
          <section class="my-4 mr-4 p-4 bg-gray-100 hover:bg-gray-200 cursor-pointer">
            <h1 class="font-bold mb-2 border-b-2">Roles</h1>
            <div
              class="flex flex-row flex-wrap"
              v-for="role in portal?.siteRoles"
              :key="role.siteRoleId"
            >
              >
              <div class="flex-1 text-sm border-b">
                {{ role.roleAlias }}
                <span v-if="role.isStaffRole" class="text-gray-500">(Staff)</span>
              </div>
            </div>
            <div v-if="!portal?.siteRoles">(No Roles. Click to Add)</div>
          </section>
        </router-link>
      </div>
      <EditPopup
        entity-name="Portal"
        :is-visible="isPopupVisible"
        :is-edit-mode="true"
        :save-error="saveError"
        :delete-enabled="false"
        @close="isPopupVisible = false"
        @save="saveClick"
      >
        <TabGroup>
          <TabList class="flex space-x-1 rounded-t-sm">
            <Tab v-slot="{ selected }">
              <button
                :class="[
                  'w-full py-2 px-4 font-medium leading-5',
                  'ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2',
                  selected
                    ? 'bg-blue-300 text-black shadow py-3'
                    : 'bg-gray-100 text-gray-800 hover:bg-blue-100  mb-1',
                ]"
              >
                General
              </button>
            </Tab>
            <Tab v-slot="{ selected }">
              <button
                :class="[
                  'w-full py-2 px-4 font-medium leading-5',
                  'ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2',
                  selected
                    ? 'bg-blue-300 text-black shadow py-3'
                    : 'bg-gray-100 text-gray-800 hover:bg-blue-100  mb-1',
                ]"
              >
                Features
              </button>
            </Tab>
            <Tab v-slot="{ selected }">
              <button
                :class="[
                  'w-full py-2 px-4 font-medium leading-5',
                  'ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2',
                  selected
                    ? 'bg-blue-300 text-black shadow py-3'
                    : 'bg-gray-100 text-gray-800 hover:bg-blue-100  mb-1',
                ]"
              >
                SSO
              </button>
            </Tab>
          </TabList>
          <TabPanels class="border border-blue-300 p-8">
            <TabPanel>
              <BaseInput
                v-model="propertyName"
                label="Portal Name"
                type="text"
                :error="errorsPortal.propertyName"
              />
              <BaseSelect
                v-model="propertyPropertyType"
                label="Property Type"
                :options="propertyTypes"
                :error="errorsPortal.propertyPropertyType"
              />
              <BaseInput
                v-model="portalSiteCode"
                label="Site Code"
                type="text"
                :error="errorsPortal.portalSiteCode"
              />
              <div class="text-gray-500 pl-6 pb-4 pt-1 font-mono text-xs italic">
                Note: documents and logo might need to be re-uploaded after SiteCode
                modification
              </div>
              <BaseInput
                v-model="portalSiteCodeAlias"
                label="Site Code Alias (optional)"
                type="text"
                :error="errorsPortal.portalSiteCodeAlias"
              />
              <BaseSelect
                :options="client?.categoryValues"
                v-model="propertyCategory"
                :label="`Client ${client?.categoryAlias ?? 'Category'}`"
                :error="errorsPortal.propertyCategory"
              />
              <BaseInput v-model="propertyFloorList" label="Floor List" type="text" />
              <BaseInput
                v-model="propertyDefaultFloor"
                label="Default Floor"
                type="text"
              />
              <BaseInput
                v-model="propertyDomainWhiteList"
                label="Domain Whitelist"
                type="text"
              />

              <BaseInput
                v-model="propertyTenantAlias"
                label="Tenant Alias"
                type="text"
                :error="errorsPortal.propertyTenantAlias"
              />
              <BaseSelect
                v-model="portalResponseLibId"
                label="Response Library"
                :options="responseLibOptions"
                :error="errorsPortal.portalResponseLibId"
                :default-option="({ name: '--None--', value: -1 } as NameValue)"
              />
            </TabPanel>
            <TabPanel>
              <h2 class="pt-2 pb-2 font-bold text-lg">Optional Features</h2>
              <BaseCheckbox
                v-model="portalIsTrainingEnabled"
                label="Enable Training"
                type="text"
              />
              <BaseCheckbox
                class="ml-1"
                v-model="portalShowExitButtonAtEndOfModule"
                label="Show Exit Button at End of Module"
                type="text"
                :disabled="!portalIsTrainingEnabled"
              />
              <BaseCheckbox
                class="ml-1"
                v-model="portalShowFinishButtonAtEndOfQuiz"
                label="Show Finish Button at End of Quiz"
                type="text"
                :disabled="!portalIsTrainingEnabled"
              />
              <BaseCheckbox
                v-model="portalIsRespondEnabled"
                label="Enable Respond"
                type="text"
              />
              <BaseCheckbox
                class="ml-1"
                v-model="portalRespondAnonView"
                label="Respond Anonymous Viewing"
                type="text"
                :disabled="!portalIsRespondEnabled"
              />
              <BaseCheckbox
                v-model="portalIsStaffDirEnabled"
                label="Enable Staff Directory"
                type="text"
              />
              <BaseCheckbox
                v-model="portalIsAlertEnabled"
                label="Enable Alerts"
                type="text"
              />
              <div v-if="portalIsAlertEnabled" class="mb-4 mx-4">
                <BaseCheckbox
                  v-model="portalIsMultiAlertEnabled"
                  label="Enable Multi-building Sending"
                  type="text"
                />
                <BaseCheckbox
                  v-model="portalIsPushNotificationsEnabled"
                  label="Enable Push Notifications"
                  type="text"
                />

                <!-- <BaseSelect
                  id="alert-phone"
                  label="Alert Origination Number"
                  v-model="portalAlertOriginationNumber"
                  :options="alertNumbers"
                  :defaultOption="alertNumbers[0]"
                >
                </BaseSelect> -->
              </div>
              <BaseCheckbox
                v-model="portalIsMobilityCheckEnabled"
                label="Enable Mobility Check"
                type="text"
              />
              <BaseCheckbox
                v-model="portalIsVrtourEnabled"
                label="Enable VR Tour"
                type="text"
              />
              <BaseCheckbox
                v-model="portalIsSharedPasswordMode"
                label="Enable Shared Password Mode"
                type="text"
              />
              <BaseCheckbox
                v-model="portalHideAllAnonymousContent"
                label="Hide All Anonymous Content"
                type="text"
              />
              <BaseCheckbox
                v-model="inviteCodeFieldsHidden"
                label="Hide Invite Code Fields"
                type="text"
              />
              <BaseCheckbox
                v-model="portalIsExpirationEmailsEnabled"
                label="Send Training Expiration Emails"
                type="text"
              />
              <div v-if="portalIsExpirationEmailsEnabled" class="mb-4 mx-4">
                <BaseSelect
                  label="Expired Training Email Template"
                  v-model="emailTemplateSettingId"
                  :options="emailTemplateOptions"
                  :default-option="{ name: '--None--', value: -1 }"
                />
              </div>

              <div class="border-t border-gray-300 m-2 w-1/2" />
              <BaseCheckbox
                v-model="portalIsActive"
                label="Portal Is Active"
                type="text"
              />
            </TabPanel>
            <TabPanel>
              <h2 class="pt-2 pb-4 font-bold text-lg">SSO Settings</h2>
              <BaseCheckbox
                label="Saml SSO Login is Enabled"
                v-model="portalIsSSOEnabled"
              />
              <BaseCheckbox
                label="Microsoft SSO Login is Enabled"
                v-model="portalIsAzureAdEnabled"
              />

              <BaseInput
                label="External Login button label"
                v-model="portalSsoButtonLabel"
              />
              <BaseInput label="SSO login URL" v-model="portalSsoLoginUrl" />
              <BaseTextarea
                placeholder="Paste cert from provider.."
                label="SSO Certificate"
                type="text"
                v-model="portalSsoCert"
              />

              <BaseInput
                label="Tenant Mapping Parameter"
                type="text"
                v-model="portalTenantMapParam"
              />

              <div class="my-4"></div>
              <BaseCheckbox
                label="Password Login is Enabled"
                v-model="portalPasswordLoginEnabled"
              />
              <BaseCheckbox
                label="Enable SSO 1st-time registration screen"
                type="text"
                v-model="portalIsSSORegistrationEnabled"
              />
            </TabPanel>
          </TabPanels>
        </TabGroup>
      </EditPopup>
    </div>
  </PortalLayout>
</template>

<script setup lang="ts">
import { TabGroup, TabList, Tab, TabPanels, TabPanel } from "@headlessui/vue";
import { useBldgStore } from "../../store/bldgStore";
import { useAdminStore } from "../../store/adminStore";
import { useRespondStore } from "../../store/respondStore";
//import { NameValue } from "../../types";
import { PropertyType, SavePortalCommand } from "../../store/generatedFetchClient";
import router from "../../router";
//import BasePopup from "../../components/BasePopup.vue";
import EditPopup from "../../components/EditPopup.vue";

import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { NameValue } from "../../sharedTypes";
import { ref, computed, onMounted, watch } from "vue";

const currentPageLocation = window.location.origin;
const adminStore = useAdminStore();
const bldgStore = useBldgStore();
const respondStore = useRespondStore();

var isLoading = ref(true);
var isPropertyLoading = computed(() => bldgStore.isLoading);
var client = computed(() =>
  adminStore.clients?.find((c) => c.clientId === portal.value?.clientId)
);
var portal = computed(() => {
  let p = bldgStore.property;
  if (p !== null && p.siteCode) {
    return p;
  }
});

onMounted(loadData);
watch(portal, loadData);

async function loadData() {
  isLoading.value = true;
  await adminStore.ensureStaffAdminStartup();
  if (portal.value?.responseLibId)
    await respondStore.getLibrary(portal.value.responseLibId);
  await respondStore.getLibraryList();
  await respondStore.getResponseTypes();
  if (portal.value?.portalId)
    await respondStore.getExcludedResponseTypes(portal.value?.portalId);

  // Load email templates
  const templates = await adminStore.getEmailTemplates();
  if (Array.isArray(templates)) {
    emailTemplateOptions.value = templates.map((t) => ({
      name: t.settingName || "Unnamed Template",
      value: t.settingId,
    }));
  }

  isLoading.value = false;
}

const responseTypeOptions = computed(() => {
  let ex = respondStore.selectedExcludedResponseTypes;
  let libItems = respondStore.selectedLibrary?.responseLibItems ?? [];

  let options: NameValue[] = [];
  libItems.forEach((i) => {
    if (
      !options.find((o) => o.value === i.responseTypeId) && // remove duplicates
      !ex.find((ex) => ex.responseTypeId === i.responseTypeId) && // handle excludes
      portal.value.siteRoles.find((sr) => sr.roleTypeId === i.roleTypeId) // only use portal site roles
    )
      options.push({ name: i.responseTypeName, value: i.responseTypeId } as NameValue);
  });

  return options;
});

// Edit popup
let isPopupVisible = ref(false);
let saveError = ref("");

let propertyTypes: NameValue[] = [
  { name: "Mixed Use", value: 1 },
  { name: "Single Tenant", value: 2 },
  { name: "Residential", value: 3 },
  { name: "Multi-Building", value: 4 },
];

// let alertNumbers = computed<NameValue[]>(() => {
//   var numValues = adminStore.originationNumbers?.map((o) => {
//     return { name: o.displayText, value: o.name };
//   });
//   console.log("Origination number values for binding to select control", numValues);
//   return numValues;
// });

let emailTemplateOptions = ref<NameValue[]>([]);

const { handleSubmit: handleSubmitPortal, errors: errorsPortal } = useForm({
  validationSchema: yup.object({
    portalSiteCode: yup.string().required().min(2).max(50).label("Site Code"),
    portalSiteCodeAlias: yup
      .string()
      .nullable()
      .notRequired()
      .max(50)
      .label("Site Code Alias"),
    portalResponseLibId: yup.number().notRequired().label("Response Library"),
    propertyName: yup.string().required().max(100).label("Portal Name"),
    propertyCategory: yup
      .string()
      .notRequired()
      .max(50)
      .label(`Client ${client.value?.categoryAlias ?? "Category"}`),
    propertyPropertyType: yup
      .mixed<PropertyType>()
      .test("not-none", "Property Type is a required field", (value) => value > 0)
      .required()
      .label("Property Type"),
    propertyTenantAlias: yup.string().notRequired().max(50).label("Tenant Alias"),
  }),
  initialValues: {
    portalSiteCode: "",
    portalSiteCodeAlias: null,
    portalResponseLibId: -1,
    propertyName: "",
    propertyCategory: "",
    propertyPropertyType: PropertyType.None,
    propertyTenantAlias: "",
  },
});

let portalId = ref(-1);
const { value: portalSiteCode } = useField<string>("portalSiteCode");
const { value: portalSiteCodeAlias } = useField<string | null>("portalSiteCodeAlias");
const { value: portalResponseLibId } = useField<number | null>("portalResponseLibId");
let portalRespondAnonView = ref(false);
let portalIsTrainingEnabled = ref(false);
let portalShowExitButtonAtEndOfModule = ref(false);
let portalShowFinishButtonAtEndOfQuiz = ref(false);
let portalIsRespondEnabled = ref(false);
let portalIsStaffDirEnabled = ref(false);
let portalIsAlertEnabled = ref(false);
let portalIsMultiAlertEnabled = ref(false);
let portalIsPushNotificationsEnabled = ref(false);
let portalAlertOriginationNumber = ref("");
let portalIsMobilityCheckEnabled = ref(false);
let portalIsVrtourEnabled = ref(false);
let portalIsSharedPasswordMode = ref(false);
let portalIsAzureAdEnabled = ref(false);
let portalIsSSOEnabled = ref(false);

let portalIsSSORegistrationEnabled = ref(false);
let portalSsoButtonLabel = ref("");
let portalSsoLoginUrl = ref("");
let portalSsoCert = ref("");
let portalTenantMapParam = ref("");
let portalPasswordLoginEnabled = ref(true);
let inviteCodeFieldsHidden = ref(false);
let portalIsExpirationEmailsEnabled = ref(false);
let emailTemplateSettingId = ref<number | null>(null);
let portalHideAllAnonymousContent = ref(false);
let portalIsActive = ref(false);

let propertyId = ref(-1);
const { value: propertyName } = useField<string>("propertyName");
const { value: propertyCategory } = useField<string>("propertyCategory");
const { value: propertyPropertyType } = useField<PropertyType>("propertyPropertyType");
let propertyFloorList = ref("");
let propertyDefaultFloor = ref("");
let propertyDomainWhiteList = ref("");
const { value: propertyTenantAlias } = useField<string>("propertyTenantAlias");

watch(portalIsRespondEnabled, () => {
  if (!portalIsRespondEnabled.value) portalRespondAnonView.value = false;
});

const responseLibOptions = computed(
  () =>
    respondStore.libraryList.map((l) => ({
      name: l.name,
      value: l.responseLibId,
    })) as NameValue[]
);

let saveClick = handleSubmitPortal(onValidSubmit, onInvalidSubmit);

async function onValidSubmit() {
  let newPortal = {
    portalId: portalId.value,
    propertyId: propertyId.value,
    clientId: portal.value.clientId,
    portalResponseLibId: portalResponseLibId.value > 0 ? portalResponseLibId.value : null,

    portalSiteCode: portalSiteCode.value,
    portalSiteCodeAlias:
      portalSiteCodeAlias.value?.length == 0 ? null : portalSiteCodeAlias.value,
    propertyName: propertyName.value,
    propertyCategory: propertyCategory.value,
    propertyPropertyType: propertyPropertyType.value.valueOf(),
    propertyFloorList: propertyFloorList.value,
    propertyDefaultFloor: propertyDefaultFloor.value,
    domainWhiteList: propertyDomainWhiteList.value,
    propertyTenantAlias: propertyTenantAlias.value,

    portalRespondAnonView: portalRespondAnonView.value,
    portalIsTrainingEnabled: portalIsTrainingEnabled.value,
    portalShowExitButtonAtEndOfModule: portalShowExitButtonAtEndOfModule.value,
    portalShowFinishButtonAtEndOfQuiz: portalShowFinishButtonAtEndOfQuiz.value,
    portalIsRespondEnabled: portalIsRespondEnabled.value,
    portalIsStaffDirEnabled: portalIsStaffDirEnabled.value,
    portalIsAlertEnabled: portalIsAlertEnabled.value,
    portalIsMultiAlertEnabled: portalIsMultiAlertEnabled.value,
    portalIsPushNotificationsEnabled: portalIsPushNotificationsEnabled.value,
    portalAlertOriginationNumber: portalAlertOriginationNumber.value,
    portalIsMobilityCheckEnabled: portalIsMobilityCheckEnabled.value,
    portalIsSSORegistrationEnabled: portalIsSSORegistrationEnabled.value,
    portalSsoButtonLabel: portalSsoButtonLabel.value,
    portalSsoCert: portalSsoCert.value,
    portalTenantMapParam: portalTenantMapParam.value,
    portalIsPasswordLoginEnabled: portalPasswordLoginEnabled.value,
    inviteCodeFieldsHidden: inviteCodeFieldsHidden.value,
    portalIsExpirationEmailsEnabled: portalIsExpirationEmailsEnabled.value,
    emailTemplateSettingId: emailTemplateSettingId.value,
    portalSsoLoginUrl: portalSsoLoginUrl.value,
    portalIsVrtourEnabled: portalIsVrtourEnabled.value,
    portalIsSharedPasswordMode: portalIsSharedPasswordMode.value,
    portalIsAzureAdEnabled: portalIsAzureAdEnabled.value,
    portalIsSSOEnabled: portalIsSSOEnabled.value,
    portalHideAllAnonymousContent: portalHideAllAnonymousContent.value,
    isActive: portalIsActive.value,
  } as SavePortalCommand;

  console.log("Saving portal with data: ", newPortal);
  saveError.value = "";

  if (portalId.value) {
    saveError.value = await adminStore.savePortal(newPortal);
    if (!saveError.value) {
      bldgStore.tryGetBuildingData(portalSiteCode.value, true);
      bldgStore.getPropertyList(true); // Refresh list for portal picker
      router.push(`/staff/${portalSiteCode.value}/portal`);
      isPopupVisible.value = false;
    }
  }
}

function onInvalidSubmit(ctx: any) {
  saveError.value = "One or more invalid form inputs.";
  console.log("error trying to submit form", ctx);
}

function editClick() {
  if (portal.value) {
    portalId.value = portal.value.portalId;
    propertyId.value = portal.value.propertyId;
    portalResponseLibId.value = portal.value?.responseLibId ?? -1;

    portalSiteCode.value = portal.value.siteCode;
    portalSiteCodeAlias.value = portal.value.siteCodeAlias;
    propertyName.value = portal.value.name;
    propertyCategory.value = portal.value.category;
    propertyPropertyType.value = portal.value.propertyType;
    propertyFloorList.value = portal.value.floorList?.join(",") ?? "";
    propertyDefaultFloor.value = portal.value.defaultFloor;
    propertyDomainWhiteList.value = portal.value.domainWhiteList?.join(", ") ?? "";
    propertyTenantAlias.value = portal.value.tenantAlias;

    portalRespondAnonView.value = portal.value.respondAnonView;
    portalIsTrainingEnabled.value = portal.value.isTrainingEnabled;
    portalShowExitButtonAtEndOfModule.value = portal.value.showExitButtonAtEndOfModule;
    portalShowFinishButtonAtEndOfQuiz.value = portal.value.showFinishButtonAtEndOfQuiz;
    portalIsRespondEnabled.value = portal.value.isRespondEnabled;
    portalIsStaffDirEnabled.value = portal.value.isStaffDirEnabled;
    portalIsAlertEnabled.value = portal.value.isAlertEnabled;
    portalIsMultiAlertEnabled.value = portal.value.isMultiAlertEnabled;
    portalIsPushNotificationsEnabled.value = portal.value.isPushNotificationsEnabled;
    portalAlertOriginationNumber.value = portal.value.alertOriginationNumber;
    portalIsSSORegistrationEnabled.value = portal.value.isSSORegistrationEnabled;
    portalSsoButtonLabel.value = portal.value.ssoButtonLabel;
    portalSsoLoginUrl.value = portal.value.ssoLoginUrl;
    portalSsoCert.value = portal.value.ssoCert;
    portalTenantMapParam.value = portal.value.tenantMapParam;
    portalPasswordLoginEnabled.value = portal.value.isPasswordLoginEnabled;
    inviteCodeFieldsHidden.value = portal.value.inviteCodeFieldsHidden;
    portalIsExpirationEmailsEnabled.value = portal.value.isExpirationEmailsEnabled;
    emailTemplateSettingId.value = portal.value.emailTemplateSettingId;

    portalIsMobilityCheckEnabled.value = portal.value.isMobilityCheckEnabled;
    portalIsVrtourEnabled.value = portal.value.isVrtourEnabled;
    portalIsSharedPasswordMode.value = portal.value.usesSharedPasswordMode ?? false;
    portalIsAzureAdEnabled.value = portal.value.isAzureAdEnabled ?? false;
    portalIsSSOEnabled.value = portal.value.isSSOEnabled ?? false;
    portalHideAllAnonymousContent.value = portal.value.hideAllAnonymousContent;

    portalIsActive.value = portal.value.isActive ?? false;

    saveError.value = "";
    isPopupVisible.value = true;
  } else {
    console.warn("Portal is null, ignoring Edit button click");
  }
}
</script>
